import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Loader2,
  Send,
  X,
  Minimize2,
  Maximize2,
  AlertCircle,
  MessageSquare,
  Trash,
  Menu,
  Zap,
  Trash2,
} from "lucide-react";
import aiService from "@/services/openRouterAIService.js";
import { parseMarkdown } from "@/utils/markdownUtils";
import "./markdown-styles.css";
import { chatService } from "@/services/chatService";
import { logger } from "@/utils/logger";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AI_POINTS } from "@/config/pointsConfig";
import usePoints from "@/hooks/usePoints";
import { cn } from "@/lib/utils";
import { useToast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { deductPoints } from "@/utils/pointsManager";
import { Skeleton } from "@/components/ui/skeleton";
import { useSettings } from "@/hooks/useSettings";
import { AlertTriangle } from "lucide-react";
import { CreditDisplay } from "@/components/ui/credit-display";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";
import { supabase } from "@/lib/supabase";
// Import MobileChatUI component
import MobileChatUI from "./MobileChatUI";

// Định nghĩa Message type để đồng bộ với AIChatPage.tsx
interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: Date | number;
  status?: "sending" | "sent" | "error";
  feedback?: "like" | "dislike";
  isTyping?: boolean; // Thêm thuộc tính isTyping
}

interface ChatWithAIProps {
  contextData?: any;
  title?: string;
  isWidget?: boolean;
  hideHeader?: boolean;
  onMessageSent?: () => void;
  fullHeight?: boolean;
  autoScrollDisabled?: boolean;
  userAvatar?: string;
  expertAvatar?: string;
  expertName?: string;
  handleClearChat?: () => void;
  welcomeMessage?: string;
  chatContainerRef?: React.RefObject<HTMLDivElement>;
  messagesEndRef?: React.RefObject<HTMLDivElement>;
  input?: string;
  setInput?: React.Dispatch<React.SetStateAction<string>>;
  handleKeyDown?: (e: React.KeyboardEvent) => void;
  handleSubmit?: (e: React.FormEvent) => void;
  isLoading?: boolean;
  className?: string;
  toggleExpertsList?: () => void;
  renderActionsBeforeInput?: () => React.ReactNode;
  inputClassName?: string;
  isMobileLayout?: boolean;
  skipSaveHistory?: boolean; // Thêm prop để bỏ qua lưu lịch sử
  hideTopHeader?: boolean; // Thêm prop để ẩn header trên cùng trong MobileChatUI
}

// Thời gian cooldown giữa các tin nhắn (ms)
const COOLDOWN_TIME = 10000; // 10 giây
// Số lượng tin nhắn tối đa trong 1 giờ
const MAX_MESSAGES_PER_HOUR = 20;

// Xử lý markdown đơn giản bằng regex
const processMarkdownContent = (content: string): string => {
  if (!content) return "";

  let processedContent = content;

  // Xử lý các tiêu đề (headers)
  // H1: # Tiêu đề
  processedContent = processedContent.replace(
    /^# (.+)$/gm,
    '<h1 class="text-3xl font-bold my-4">$1</h1>',
  );
  // H2: ## Tiêu đề
  processedContent = processedContent.replace(
    /^## (.+)$/gm,
    '<h2 class="text-2xl font-bold my-3">$1</h2>',
  );
  // H3: ### Tiêu đề
  processedContent = processedContent.replace(
    /^### (.+)$/gm,
    '<h3 class="text-xl font-bold my-2">$1</h3>',
  );
  // H4: #### Tiêu đề
  processedContent = processedContent.replace(
    /^#### (.+)$/gm,
    '<h4 class="text-lg font-bold my-2">$1</h4>',
  );
  // H5: ##### Tiêu đề
  processedContent = processedContent.replace(
    /^##### (.+)$/gm,
    '<h5 class="text-base font-bold my-1">$1</h5>',
  );
  // H6: ###### Tiêu đề
  processedContent = processedContent.replace(
    /^###### (.+)$/gm,
    '<h6 class="text-sm font-bold my-1">$1</h6>',
  );

  // Xử lý in đậm (**text** hoặc __text__)
  processedContent = processedContent.replace(
    /\*\*(.*?)\*\*|__(.*?)__/g,
    "<strong>$1$2</strong>",
  );

  // Xử lý in nghiêng (*text* hoặc _text_)
  processedContent = processedContent.replace(
    /\*(.*?)\*|_(.*?)_/g,
    "<em>$1$2</em>",
  );

  // Xử lý gạch ngang (~~ text ~~)
  processedContent = processedContent.replace(/~~(.*?)~~/g, "<del>$1</del>");

  // Xử lý danh sách không thứ tự
  processedContent = processedContent.replace(
    /^\s*[-*+]\s+(.+)$/gm,
    "<li>$1</li>",
  );
  // Nhóm các <li> thành <ul>
  processedContent = processedContent.replace(
    /(<li>.*?<\/li>)\s*\n\s*(?!<li>)/gs,
    '<ul class="list-disc pl-5 my-2">$1</ul>\n',
  );

  // Xử lý danh sách có thứ tự
  processedContent = processedContent.replace(
    /^\s*(\d+)\.\s+(.+)$/gm,
    "<li>$2</li>",
  );
  // Nhóm các <li> có thứ tự thành <ol>
  processedContent = processedContent.replace(
    /(<li>.*?<\/li>)\s*\n\s*(?!<li>)/gs,
    '<ol class="list-decimal pl-5 my-2">$1</ol>\n',
  );

  // Xử lý link [text](url)
  processedContent = processedContent.replace(
    /\[(.*?)\]\((.*?)\)/g,
    '<a href="$2" class="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">$1</a>',
  );

  // Xử lý blockquote
  processedContent = processedContent.replace(
    /^>\s+(.+)$/gm,
    '<blockquote class="border-l-4 border-gray-300 pl-4 my-2 italic">$1</blockquote>',
  );

  // Xử lý xuống dòng (2 dấu cách cuối dòng hoặc dòng trống)
  processedContent = processedContent.replace(/  $/gm, "<br />");
  processedContent = processedContent.replace(/\n\n/g, '<p class="my-2"></p>');

  // Xử lý code inline
  processedContent = processedContent.replace(
    /`([^`]+)`/g,
    '<code class="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">$1</code>',
  );

  // Xử lý code block (cơ bản, không hỗ trợ highlighting)
  processedContent = processedContent.replace(
    /```(?:\w+)?\n([\s\S]+?)\n```/g,
    '<pre class="bg-gray-100 p-3 rounded my-3 overflow-x-auto font-mono text-sm">$1</pre>',
  );

  return processedContent;
};

// Thêm CSS cho welcome message container và hiệu ứng typing mới
const expert_greeting_style = `
  .expert-info-compact {
    font-size: 15px;
  }
  
  .expert-description {
    margin-top: 6px;
    line-height: 1.4;
    color: #666;
  }
  
  .welcome-message-container {
    max-width: 95% !important;
    background-color: #f8f8f8 !important;
  }
  
  .welcome-message-body {
    background-color: transparent !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
  
  .welcome-content {
    font-size: 15px;
    line-height: 1.5;
  }
  
  .chat-card-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    box-shadow: none !important;
    background-color: transparent !important;
  }
  
  /* Animated typing dots cho tin nhắn đang gõ */
  .typing-dots-container {
    display: flex;
    align-items: center;
    margin: 8px 0 2px 5px;
    height: 26px;
  }
  
  .typing-dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    margin-right: 4px;
    border-radius: 50%;
    background-color: #666;
    opacity: 0.8;
    animation: typingAnimation 1.4s infinite ease-in-out;
  }
  
  .dark .typing-dot {
    background-color: #aaa;
  }
  
  .typing-dot:nth-child(1) { animation-delay: 0s; }
  .typing-dot:nth-child(2) { animation-delay: 0.2s; }
  .typing-dot:nth-child(3) { animation-delay: 0.4s; }
  
  @keyframes typingAnimation {
    0%, 60%, 100% { transform: translateY(0); opacity: 0.8; }
    30% { transform: translateY(-6px); opacity: 1; }
  }
  
  /* Hiệu ứng typing indicator mới */
  .typing-indicator {
    display: flex;
    align-items: center;
    column-gap: 4px;
  }
  
  .typing-indicator .dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #666;
    animation: bounce 1.4s infinite ease-in-out;
  }
  
  .dark .typing-indicator .dot {
    background-color: #aaa;
  }
  
  .typing-indicator .dot:nth-child(1) { animation-delay: 0s; }
  .typing-indicator .dot:nth-child(2) { animation-delay: 0.2s; }
  .typing-indicator .dot:nth-child(3) { animation-delay: 0.4s; }
  
  @keyframes bounce {
    0%, 60%, 100% { transform: translateY(0); }
    30% { transform: translateY(-4px); }
  }
  
  /* Style cho chat header trong nội dung */
  .chat-inner-header {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    margin-bottom: 10px;
    border-radius: 8px;
    background-color: #f0f0f0;
    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  }
  
  .dark .chat-inner-header {
    background-color: #2a2a2a;
  }
  
  /* Chat controls above input */
  .chat-controls {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    border-top: 1px solid #f0f0f0;
    background-color: white;
    font-size: 13px;
  }
  
  .chat-controls-left, .chat-controls-right {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .chat-control-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    color: #666;
    font-size: 13px;
    background: none;
    border: none;
    padding: 6px 8px;
    border-radius: 4px;
    cursor: pointer;
  }
  
  .chat-control-btn:hover {
    background-color: #f5f5f5;
  }
  
  /* Cố định input chat trên bottom nav */
  @media (max-width: 767px) {
    /* Card content should fill available space */
    .card-content-mobile {
      padding-bottom: 120px !important; /* Giảm padding để tránh khoảng trống thừa */
      padding-top: 6px !important;
      overflow-y: auto !important;
      height: calc(100% - 51px) !important;
    }
    
    /* Fixed input at bottom */
    .fixed-input-container {
      position: fixed !important;
      bottom: 75px !important; /* Tăng lên để tránh bị che bởi bottom-nav */
      left: 0 !important;
      right: 0 !important;
      padding: 8px 15px 15px 15px !important; /* Điều chỉnh padding */
      background-color: white !important;
      border-top: 1px solid #eee !important;
      box-shadow: 0 -1px 2px rgba(0,0,0,0.05) !important;
      z-index: 50 !important;
      width: 100% !important;
    }
    
    /* Thêm padding cho phần cuối danh sách tin nhắn */
    .messages-wrapper {
      padding-bottom: 120px !important; /* Giảm giá trị để tránh khoảng trống thừa */
      margin-left: 0 !important;
    }
    
    /* Đảm bảo không có viền và background cho card trong mobile */
    .chat-card-container {
      border: none !important;
      background-color: transparent !important;
    }
    
    /* Styling for compact message layout */
    .message-body {
      padding: 4px 8px !important;
    }
    
    /* Make header sticky */
    .chat-inner-header {
      position: -webkit-sticky !important;
      position: sticky !important;
      top: 0 !important;
      z-index: 40 !important;
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      margin: 0 -10px 10px -10px !important;
      border-radius: 0 !important;
      padding: 10px 15px !important;
    }
    
    /* Mobile chat message containers */
    .chat-message {
      max-width: 100% !important;
      padding: 6px 8px !important;
      margin-bottom: 6px !important;
    }
  }

  /* Style cho phần credits và clear history */
  .fixed-input-container {
    display: flex;
    flex-direction: column;
  }

  /* Fixed footer cho mobile */
  .mobile-chat-footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 50;
    background-color: white;
    border-top: 1px solid #eee;
    padding-bottom: env(safe-area-inset-bottom, 16px);
  }
  
  .mobile-chat-footer-with-bottom-nav {
    bottom: 70px; /* Tăng khoảng cách lên để tránh đè lên bottom navigation */
  }
  
  /* Thêm padding cho phần dưới tin nhắn khi có mobile footer */
  .mobile-chat-content {
    padding-bottom: 120px !important; /* Giảm padding xuống để tránh khoảng trống thừa */
  }

  /* Điều chỉnh bottom-nav container để tránh chồng lấn */
  .bottom-nav-container {
    z-index: 60 !important; /* Đảm bảo bottom nav luôn hiển thị trên cùng */
  }
  
  /* Style cho phần actions trên input */
  .actions-before-input {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 16px;
    border-bottom: 1px solid #eee;
  }
  
  .clear-history-button {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    color: #555;
    padding: 6px 12px;
    border-radius: 4px;
    background-color: #f5f5f5;
    font-weight: 500;
  }
  
  .clear-history-icon {
    margin-right: 4px;
    width: 16px;
    height: 16px;
  }
`;

// Thêm hàm tạo unique ID ở đầu file (sau phần import và trước các interface)
// Tạo hàm tạo unique ID để đảm bảo không bị trùng lặp
const generateUniqueId = (prefix: string = '') => {
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 10000000).toString().padStart(7, '0');
  const randomChars = Math.random().toString(36).substring(2, 8);
  const secondRandom = Math.floor(Math.random() * 1000000).toString(36);
  return `${prefix}_${timestamp}_${random}_${randomChars}_${secondRandom}`;
};

// Chuyển đổi thành forwardRef để component có thể nhận ref từ component cha
const ChatWithAI = forwardRef<any, ChatWithAIProps>(
  (
    {
      contextData = {},
      title = "Thiền Sư AI",
      isWidget = false,
      hideHeader = false,
      onMessageSent,
      fullHeight = false,
      autoScrollDisabled = false,
      userAvatar = "/assets/icons/user-avatar.png",
      expertAvatar = "https://imagedelivery.net/meCI2KDHmnUhVs_SxNm-Og/a1d82958-cc6f-4bdc-ce0b-25bdc7b27500/public",
      expertName = "AI",
      handleClearChat,
      welcomeMessage,
      chatContainerRef,
      messagesEndRef: passedMessagesEndRef,
      input: initialInput = "",
      setInput: passedSetInput,
      handleKeyDown: passedHandleKeyDown,
      handleSubmit: passedHandleSubmit,
      isLoading: initialIsLoading = false,
      className,
      toggleExpertsList,
      renderActionsBeforeInput,
      inputClassName,
      isMobileLayout = false,
      skipSaveHistory = false, // Thêm prop để bỏ qua lưu lịch sử
      hideTopHeader = false, // Thêm prop để ẩn header trên cùng trong MobileChatUI
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [conversationId, setConversationId] = useState("");
    const [isMinimized, setIsMinimized] = useState(false);
    const [cooldownRemaining, setCooldownRemaining] = useState(0);
    const [messageCount, setMessageCount] = useState(0);
    const [isRateLimited, setIsRateLimited] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const cooldownTimerRef = useRef<NodeJS.Timeout | null>(null);
    const typingTimerRef = useRef<NodeJS.Timeout | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const { toast } = useToast();
    const { user } = useAuth();
    const { settings } = useSettings();
    const [input, setInput] = useState(initialInput);
    const [isLoading, setIsLoading] = useState(initialIsLoading);
    const internalMessagesEndRef = useRef<HTMLDivElement>(null);
    const messagesEndRef = passedMessagesEndRef || internalMessagesEndRef;
    const [isMobile, setIsMobile] = useState(false);
    const [isInitialRender, setIsInitialRender] = useState(true);
    const [isWaitingForGreeting, setIsWaitingForGreeting] = useState(false);
    const [forceRenderKey, setForceRenderKey] = useState(0);
    const [userCredits, setUserCredits] = useState(0);
    const [contextInfo, setContextInfo] = useState(null);

    // Kiểm tra nếu là mobile
    useEffect(() => {
      const checkIfMobile = () => {
        setIsMobile(window.innerWidth <= 768);
      };

      checkIfMobile();
      window.addEventListener("resize", checkIfMobile);

      return () => window.removeEventListener("resize", checkIfMobile);
    }, []);

    // Sử dụng hook usePoints
    const { checkAndDeductForChat } = usePoints();

    // Scroll to bottom whenever messages change, but not on initial render
    useEffect(() => {
      // Skip auto-scrolling if autoScrollDisabled or if it's the initial render
      if (autoScrollDisabled || isInitialRender) {
        // Mark that initial render is done
        setIsInitialRender(false);
        return;
      }

      // Chỉ scroll to bottom khi có tin nhắn mới từ người dùng hoặc tin nhắn mới hoàn chỉnh từ AI
      if (messages.length > 0 && messagesEndRef?.current) {
        const lastMessage = messages[messages.length - 1];
        
        // Chỉ scroll khi có tin nhắn mới từ người dùng hoặc tin nhắn mới hoàn chỉnh từ AI, không scroll khi load lại trang
        if (lastMessage && (lastMessage.role === "user" || (lastMessage.role === "assistant" && !lastMessage.isTyping))) {
          // Sử dụng scrollIntoView với behavior "smooth" để tạo trải nghiệm mượt mà hơn
          messagesEndRef.current.scrollIntoView({ 
            behavior: "smooth", 
            block: "end" // Đảm bảo scroll đến cuối tin nhắn
          });
        }
      }
    }, [messages, autoScrollDisabled, isInitialRender]);

    // Tạo storage key dựa trên thông tin chuyên gia và khóa học
    const getStorageKey = () => {
      const expertId = contextData?.expertId || "default";
      const contextType = contextData?.contextType || "general";
      const contextId = contextData?.contextId || "";
      const courseId =
        contextData?.course_contents?.id ||
        contextData?.lessonData?.courseId ||
        contextData?.contextId ||
        "";

      // Tạo key dựa trên khóa học và chuyên gia
      return `ai-chat-${contextType}-${courseId}-${expertId}`;
    };

    // Khai báo addWelcomeMessage bằng useCallback để tránh lỗi "Cannot access before initialization"
    const addWelcomeMessage = useCallback(() => {
      // Tạo ID duy nhất cho welcome message
      const welcomeId = generateUniqueId('welcome');
      
      // Chuẩn bị nội dung
      const welcomeContent = contextData?.expertGreeting || 
                            (contextData?.expertName ? 
                              `Xin chào! Tôi là ${contextData.expertName}, bạn có thể hỏi tôi về nội dung này.` : 
                              "Xin chào! Tôi có thể giúp gì cho bạn?");
      
      // Tạo tin nhắn welcome
      const welcomeMessage: ChatMessage = {
        id: welcomeId,
        role: "assistant",
        content: welcomeContent,
        timestamp: new Date(),
      };
      
      // Thêm vào đầu danh sách tin nhắn, thay thế tin nhắn welcome cũ nếu có
      setMessages(prevMessages => {
        // Kiểm tra xem đã có tin nhắn welcome chưa
        const hasWelcomeMessage = prevMessages.some(msg => msg.id && msg.id.includes('welcome'));
        
        if (hasWelcomeMessage) {
          // Thay thế tin nhắn welcome cũ
          return [
            welcomeMessage,
            ...prevMessages.filter(msg => !(msg.id && msg.id.includes('welcome')))
          ];
        } else {
          // Thêm tin nhắn welcome mới vào đầu
          return [welcomeMessage, ...prevMessages];
        }
      });
    }, [contextData?.expertGreeting, contextData?.expertName]);

    // Load conversation from localStorage based on expertId
    useEffect(() => {
      const expertId = contextData?.expertId || 'general';
      
      // Biến để kiểm soát việc thêm welcome message
      let hasLoadedMessages = false;
      
      // Kiểm tra nếu vừa mới xóa lịch sử (từ sessionStorage)
      try {
        const justClearedChat = sessionStorage.getItem('just_cleared_chat');
        if (justClearedChat) {
          // Xóa marker
          sessionStorage.removeItem('just_cleared_chat');
          
          console.log("ChatWithAI: Phát hiện vừa xóa lịch sử, sẽ thêm welcome message mới");
          addWelcomeMessage();
          return; // Không load lịch sử nữa
        }
      } catch (e) {
        // Bỏ qua lỗi sessionStorage
      }
      
      // Tải lịch sử từ cookie dựa theo expertId
      try {
        if (typeof window !== 'undefined') {
          // Nhập service
          import('@/services/OpenRouterServiceChat.js').then((module) => {
            const { getChatHistoryFromCookies } = module;
            
            // Lấy lịch sử từ cookie
            const history = getChatHistoryFromCookies(expertId);
            
            if (history && Array.isArray(history.messages) && history.messages.length > 0) {
              console.log(`Đã load ${history.messages.length} tin nhắn từ cookie cho expert ${expertId}`);
              
              // Cập nhật context info từ lịch sử nếu có
              if (history.contextInfo) {
                setContextInfo(history.contextInfo);
              }
              
              // Cập nhật tin nhắn
              setMessages(history.messages);
              hasLoadedMessages = true;
              
              // Nếu có conversationId trong lịch sử, sử dụng nó
              if (history.conversationId) {
                setConversationId(history.conversationId);
              }
            } else {
              console.log(`Không tìm thấy lịch sử cho expert ${expertId}, thêm welcome message`);
              addWelcomeMessage();
            }
          }).catch(error => {
            console.error("Lỗi khi load lịch sử chat:", error);
            addWelcomeMessage();
          });
        }
      } catch (error) {
        console.error("Lỗi khi tải lịch sử chat:", error);
        addWelcomeMessage();
      }
      
      // Cleanup
      return () => {
        // Không cần cleanup đặc biệt
      };
    }, [contextData?.expertId, addWelcomeMessage]);

    // Thêm hàm kiểm tra lỗi conversation không tồn tại
    const isConversationNotExistsError = (error: any): boolean => {
      // Kiểm tra nếu là Error object
      if (error instanceof Error) {
        const errorMessage = error.message.toLowerCase();
        return (
          errorMessage.includes("conversation not exists") ||
          errorMessage.includes("status: 404") ||
          errorMessage.includes("not_found") ||
          errorMessage.includes("404")
        );
      }

      // Kiểm tra nếu là object có thuộc tính code, status hoặc message
      if (error && typeof error === "object") {
        // Kiểm tra status code
        if ("status" in error && error.status === 404) {
          return true;
        }

        // Kiểm tra code
        if ("code" in error && error.code === "not_found") {
          return true;
        }

        // Kiểm tra message
        if ("message" in error && typeof error.message === "string") {
          const errorMessage = error.message.toLowerCase();
          return (
            errorMessage.includes("conversation not exists") ||
            errorMessage.includes("not found")
          );
        }
      }

      return false;
    };

    // Hàm mới để xử lý tin nhắn (truyền vào message text)
    const sendMessageDirectly = async (messageText: string) => {
      console.log("sendMessageDirectly called with message:", messageText);
      
      if (!messageText || !messageText.trim()) {
        console.log("Message text is empty, not sending");
        return;
      }

      // Kiểm tra rate limit
      if (isRateLimited) {
        alert(t("ai:rate_limit_exceeded"));
        return;
      }

      // Kiểm tra cooldown - chỉ hiển thị alert khi cooldown > 0
      if (cooldownRemaining > 0) {
        const seconds = Math.ceil(cooldownRemaining / 1000);
        if (seconds > 0) {
          alert(t("ai:please_wait", { seconds }));
          return;
        }
      }
      
      // Kiểm tra và trừ điểm trước khi hiển thị tin nhắn
      const pointsResult = await checkAndDeductForChat(
        contextData?.contextType || "basic",
        contextData?.contextId,
        contextData?.expertName,
        contextData?.contextName,
      );

      if (!pointsResult.success) {
        setError(pointsResult.message);
        return;
      }

      // Tạo tin nhắn từ người dùng với ID duy nhất
      const userMessage = {
        id: generateUniqueId('user'),
        role: "user" as "user",
        content: messageText,
        timestamp: new Date(),
      };

      // Thêm tin nhắn vào danh sách
      setMessages((prev) => [...prev, userMessage]);
      
      // Cập nhật input state - để đồng bộ UI
      setInput("");
      
      // Cập nhật trạng thái
      setIsLoading(true);

      // Cập nhật message count
      const newMessageCount = messageCount + 1;
      setMessageCount(newMessageCount);
      localStorage.setItem("ai-chat-message-count", newMessageCount.toString());

      // Cập nhật timestamp nếu chưa có
      if (!localStorage.getItem("ai-chat-message-count-timestamp")) {
        localStorage.setItem(
          "ai-chat-message-count-timestamp",
          Date.now().toString(),
        );
      }

      // Kiểm tra nếu đã đạt giới hạn
      if (newMessageCount >= MAX_MESSAGES_PER_HOUR) {
        setIsRateLimited(true);
      }

      // Bắt đầu cooldown
      setCooldownRemaining(COOLDOWN_TIME);

      try {
        // Hàm giúp chuyển đổi bất kỳ giá trị nào thành chuỗi an toàn
        const toSafeString = (value) => {
          try {
            if (value === null || value === undefined) return "";
            if (typeof value === "string") return value;
            if (typeof value === "object") {
              try {
                return JSON.stringify(value);
              } catch (e) {
                console.warn("Không thể chuyển đổi object thành JSON:", e);
                return String(value || "");
              }
            }
            return String(value);
          } catch (error) {
            console.error("Lỗi trong toSafeString:", error);
            return "";
          }
        };

        // Log chi tiết dữ liệu context để debug
        console.log("Context data for AI request:", {
          expertId: contextData?.expertId || contextData?.expert_id || "không có",
          contextType: contextData?.contextType || "không có",
          contextName: contextData?.contextName || contextData?.lessonData?.contentName || contextData?.course_contents?.name || "không có",
        });

        // Đảm bảo contextData là object
        const safeContextData = contextData || {};

        // Trích xuất course_id từ contextData
        const courseId =
          safeContextData.course_contents?.id ||
          safeContextData.lessonData?.courseId ||
          safeContextData.courseId ||
          safeContextData.contextId;

        // Lấy chat history (tối đa 10 tin nhắn gần nhất)
        const chatHistory = messages
          .filter((msg) => !msg.isTyping) // Loại bỏ tin nhắn đang typing
          .slice(-10) // Chỉ lấy 10 tin nhắn gần nhất
          .map((msg) => ({
            role: msg.role,
            content: msg.content,
          }));

        // Truyền dữ liệu context với đầy đủ thông tin
        const data = {
          // ID chuyên gia - ưu tiên lấy từ contextData.expertId, nếu không có thì lấy từ expert_id
          expert_id:
            safeContextData.expertId || safeContextData.expert_id || null,

          // Nội dung bài học - kết hợp từ các nguồn có sẵn hoặc mặc định là chuỗi rỗng
          content_info: toSafeString(
            safeContextData.contextData ||
              safeContextData.lessonData?.contentData ||
              safeContextData.course_contents?.description ||
              "",
          ),

          // Tên bài học - kết hợp từ các nguồn có sẵn hoặc mặc định là chuỗi rỗng
          lessonName: toSafeString(
            safeContextData.contextName ||
              safeContextData.lessonData?.contentName ||
              safeContextData.course_contents?.name ||
              "",
          ),

          // ID khóa học - để server có thể lấy nội dung bổ sung
          course_id: courseId || null,

          // Chat history - tối đa 10 tin nhắn gần nhất
          chat_history: chatHistory,

          // ID conversation nếu có
          conversation_id: conversationId || null,
        };

        // Thực hiện gọi API để gửi tin nhắn
        let response;
        try {
          console.log("Calling AI service with message:", messageText);
          // Chỉ gửi conversation_id nếu có giá trị
          const currentConversationId = conversationId && conversationId.trim() !== "" ? conversationId : "";
          console.log("Using conversation ID:", currentConversationId);
          
          response = await aiService.chatWithAI(messageText, {
            ...data,
            conversation_id: currentConversationId,
          });
          
          console.log("Received response from AI:", response);
        } catch (initialError) {
          console.error("Error in initial chat request:", initialError);

          // Kiểm tra nếu lỗi là conversation không tồn tại
          if (isConversationNotExistsError(initialError)) {
            console.log(
              "Conversation not found, retrying with empty conversation ID"
            );

            try {
              // Gọi lại API với conversation_id rỗng
              console.log("Retrying with empty conversation ID");

              // Đảm bảo input được xử lý an toàn
              const safeInput = messageText ? messageText.trim() : "";

              if (!safeInput) {
                throw new Error("Input is empty after retry");
              }

              // Cập nhật tham số gọi hàm chatWithAI sang 2 tham số
              response = await aiService.chatWithAI(safeInput, {
                ...data,
                conversation_id: "",
              });

              // Đảm bảo response không trống
              if (!response || !response.answer) {
                console.error(
                  "Empty response from AI service on retry:",
                  response,
                );
                throw new Error("Empty response from AI service on retry");
              }
            } catch (retryError) {
              console.error("Error in retry chat request:", retryError);
              throw retryError; // Ném lại lỗi để xử lý ở catch bên ngoài
            }
          } else {
            // Nếu là lỗi khác, ném lại lỗi
            throw initialError;
          }
        }

        if (response) {
          // Save conversation ID if it's a new conversation or if it changed
          if (
            response.conversation_id &&
            response.conversation_id !== conversationId
          ) {
            console.log(
              "Saving new conversation ID:",
              response.conversation_id,
            );
            setConversationId(response.conversation_id);
            const storageKeyPrefix = getStorageKey();
            localStorage.setItem(
              `${storageKeyPrefix}-conversation-id`,
              response.conversation_id,
            );
          }

          // Lấy nội dung phản hồi từ AI
          const messageId = response.message_id || generateUniqueId('ai');
          let content = "";

          try {
            content =
              response.answer ||
              "Xin lỗi, thầy không thể xử lý yêu cầu của con lúc này.";

            // Làm sạch nội dung
            if (typeof content === "string") {
              if (content.startsWith("text")) {
                content = content.substring(4).trim();
              }

              // Loại bỏ code block nếu toàn bộ nội dung được bọc trong ```
              if (content.startsWith("```") && content.endsWith("```")) {
                content = content.substring(3, content.length - 3).trim();
              }
            } else {
              console.warn("AI response content is not a string:", content);
              content =
                "Xin lỗi, thầy không thể xử lý yêu cầu của con lúc này.";
            }
          } catch (error) {
            console.error("Error processing AI response content:", error);
            content = "Xin lỗi, thầy không thể xử lý yêu cầu của con lúc này.";
          }

          // Simulate typing cho tin nhắn AI
          try {
            simulateTyping(content, messageId);

            // Lưu tin nhắn vào localStorage
            saveMessagesToLocalStorage([
              ...messages,
              userMessage,
              {
                id: messageId,
                role: "assistant" as "assistant",
                content: content,
                timestamp: new Date(),
                isTyping: false,
              },
            ]);
          } catch (error) {
            console.error("Error simulating typing or saving messages:", error);

            // Fallback: Thêm tin nhắn thông thường nếu simulateTyping lỗi
            setMessages((prev) => [
              ...prev,
              {
                id: messageId,
                role: "assistant",
                content: content,
                timestamp: new Date(),
              },
            ]);
          }

          // Thông báo cho component cha nếu có
          if (onMessageSent) {
            onMessageSent();
          }
        }
      } catch (error) {
        console.error("Error sending message to AI:", error);

        // Tạo tin nhắn lỗi để hiển thị cho người dùng
        let errorMessage;

        try {
          // Kiểm tra nếu lỗi là conversation không tồn tại
          if (isConversationNotExistsError(error)) {
            // Reset conversation ID
            console.log(
              "Conversation not found in error handler, resetting conversation ID",
            );
            setConversationId("");

            try {
              const storageKeyPrefix = getStorageKey();
              localStorage.removeItem(`${storageKeyPrefix}-conversation-id`);
            } catch (storageError) {
              console.error(
                "Error removing conversation ID from localStorage:",
                storageError,
              );
            }

            // Thêm thông báo lỗi và gợi ý
            errorMessage = {
              id: Date.now().toString(),
              role: "assistant",
              content:
                "Xin lỗi con, cuộc trò chuyện trước đã hết hạn. Thầy đã tạo cuộc trò chuyện mới. Hãy gửi lại câu hỏi của con nhé.",
              timestamp: new Date(),
            };
          } else {
            // Add general error message
            errorMessage = {
              id: Date.now().toString(),
              role: "assistant",
              content:
                "Xin lỗi con, thầy không thể xử lý yêu cầu của con lúc này. Vui lòng thử lại sau.",
              timestamp: new Date(),
            };
          }

          // Thêm tin nhắn lỗi vào messages state
          setMessages((prev) => [...prev, errorMessage]);

          // Lưu tin nhắn lỗi vào localStorage
          try {
            saveMessagesToLocalStorage([
              ...messages,
              userMessage,
              errorMessage,
            ]);
          } catch (saveError) {
            console.error(
              "Error saving error message to localStorage:",
              saveError,
            );
          }
        } catch (handlerError) {
          console.error("Error in error handler:", handlerError);

          // Fallback khi xử lý lỗi thất bại
          try {
            setMessages((prev) => [
              ...prev,
              {
                id: Date.now().toString(),
                role: "assistant",
                content:
                  "Đã xảy ra lỗi không mong muốn. Vui lòng làm mới trang và thử lại.",
                timestamp: new Date(),
              },
            ]);
          } catch (finalError) {
            console.error("Fatal error in ChatWithAI component:", finalError);
          }
        }
      } finally {
        setIsLoading(false);

        // Đảm bảo cooldown được reset sau khi nhận response hoặc khi có lỗi
        try {
          if (cooldownTimerRef.current) {
            clearInterval(cooldownTimerRef.current);
            cooldownTimerRef.current = null;
          }
          setCooldownRemaining(0);
        } catch (timerError) {
          console.error("Error resetting cooldown timer:", timerError);
        }
      }
    };

    // Xử lý khi nhấn Enter
    const handleKeyDown = (e: React.KeyboardEvent) => {
      // Sử dụng handler từ props nếu được truyền vào
      if (passedHandleKeyDown) {
        passedHandleKeyDown(e);
        return;
      }

      // Nếu không có handler từ props, sử dụng xử lý mặc định
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    };

    // Cập nhật input khi có thay đổi
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      try {
        const newValue = e.target.value || '';
        // console.log("Input changed to:", newValue, "Length:", newValue.length);
        
        // Cập nhật state
        setInput(newValue);
        
        // Cập nhật setInput từ props nếu được truyền vào
        if (passedSetInput) {
          passedSetInput(newValue);
        }
      } catch (error) {
        console.error("Error in handleInputChange:", error);
        // Fallback
        setInput('');
        if (passedSetInput) {
          passedSetInput('');
        }
      }
    };

    // Thêm hàm clearChatHistoryHelper để xóa cookie chat history
    const clearChatHistoryHelper = (expertId: string) => {
      try {
        // Phương pháp 1: Xóa cookie trực tiếp với expired date
        document.cookie = `chat_history_${expertId}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax`;
        
        // Phương pháp 2: Ghi đè bằng mảng rỗng
        document.cookie = `chat_history_${expertId}=[];path=/;max-age=1`;
        
        // Phương pháp 3: Import module OpenRouterServiceChat
        import('@/services/OpenRouterServiceChat.js').then((service) => {
          if (service.clearChatHistory) {
            service.clearChatHistory(expertId);
          }
        }).catch(error => {
          console.error("Error importing OpenRouterServiceChat module:", error);
        });
        
        console.log(`Đã xóa cookie chat_history_${expertId}`);
        return true;
      } catch (error) {
        console.error(`Lỗi khi xóa cookie chat_history_${expertId}:`, error);
        return false;
      }
    };

    // Sửa hàm clearConversation
    const clearConversation = (forceReload = true) => {
      // Dừng ngay mọi loading state
      setIsLoading(false);
      
      // Reset trạng thái đợi greeting để xóa skeleton loading
      setIsWaitingForGreeting(false);
      
      // Dừng bất kỳ timeout/interval đang chạy
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
        cooldownTimerRef.current = null;
      }
      
      // Reset cooldown
      setCooldownRemaining(0);
      
      const expertId = contextData?.expertId || "general";
      console.log(`clearConversation: Đang xóa lịch sử chat cho expert ${expertId}`);
      
      try {
        // Xóa tin nhắn trong state
        setMessages([]);
        
        // Reset conversation ID (sử dụng chuỗi rỗng thay vì null)
        setConversationId("");
        
        // Xóa cookie sử dụng helper function
        clearChatHistoryHelper(expertId);
        
        // Đặt marker vào sessionStorage để biết đã xóa lịch sử
        try {
          sessionStorage.setItem("just_cleared_chat", "true");
        } catch (e) {
          // Bỏ qua lỗi sessionStorage
          console.error("Không thể đặt marker trong sessionStorage:", e);
        }
        
        // Clear context info
        setContextInfo(null);
        
        // Hiển thị toast để thông báo
        toast({
          title: t("ai.history_cleared", "Đã xóa lịch sử"),
          description: "Lịch sử trò chuyện đã được xóa thành công.",
          duration: 3000,
        });
        
        // Thêm welcome message mới
        addWelcomeMessage();
        
        // Force reload trang nếu cần thiết
        if (forceReload) {
          console.log("clearConversation: Force reload trang...");
          setTimeout(() => {
            window.location.reload();
          }, 500);
        }
        
        return true;
      } catch (error) {
        console.error("Lỗi khi xóa lịch sử chat:", error);
        
        // Hiển thị thông báo lỗi
        toast({
          title: "Lỗi khi xóa lịch sử",
          description: "Đã xảy ra lỗi khi xóa lịch sử trò chuyện. Vui lòng thử lại.",
          variant: "destructive",
          duration: 5000,
        });
        
        return false;
      }
    }

    // Sửa hàm handleClearHistoryClick
    const handleClearHistoryClick = () => {
      // Dừng ngay mọi hoạt động
      setIsLoading(false);
      
      // Reset trạng thái đợi greeting để xóa hiệu ứng loading skeleton
      setIsWaitingForGreeting(false);
      
      // Hỏi xác nhận trước khi xóa lịch sử
      const confirmMessage = t("ai.confirm_clear_history", 
        "Bạn có chắc muốn xóa lịch sử trò chuyện này không? Trang sẽ được tải lại sau khi xóa."
      );
      
      if (window.confirm(confirmMessage)) {
        try {
          // Thông báo đang xóa lịch sử
          toast({
            title: "Đang xóa lịch sử...",
            description: "Trang sẽ được tải lại sau khi xóa xong.",
          });
          
          // Đặt marker trong sessionStorage để biết đã xóa lịch sử
          try {
            sessionStorage.setItem('just_cleared_chat', 'true');
          } catch (e) {
            console.error("Lỗi khi đặt marker trong sessionStorage:", e);
          }
          
          // Xóa lịch sử và reload trang
          const expertId = contextData?.expertId || 'general';
          console.log(`Đang xóa lịch sử chat cho expert ${expertId}...`);
          
          // Xóa history với helper function
          clearChatHistoryHelper(expertId);
          
          // Reload trang sau khi xóa
          setTimeout(() => {
            window.location.reload();
          }, 500);
        } catch (error) {
          console.error("Lỗi khi xóa lịch sử:", error);
          
          // Reload cho dù có lỗi
          setTimeout(() => {
            window.location.reload();
          }, 500);
        }
      }
    };

    // Hàm xử lý khi click Change expert
    const handleChangeExpertClick = () => {
      if (toggleExpertsList) {
        toggleExpertsList();
      }
    };

    // Widget style
    const widgetStyle = isWidget
      ? ({
          position: "fixed",
          bottom: isMinimized ? "20px" : "30px",
          right: "30px",
          width: isMinimized ? "auto" : "350px",
          maxWidth: "90vw",
          height: isMinimized ? "auto" : "500px", // Tăng chiều cao để đảm bảo input hiển thị đầy đủ
          maxHeight: "80vh", // Tăng maxHeight để có thêm không gian
          zIndex: 1000,
          boxShadow:
            "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          borderRadius: "12px",
          transition: "all 0.3s ease",
          backgroundColor: "hsl(var(--background))",
          border: "1px solid hsl(var(--border))",
          display: "flex",
          flexDirection: "column",
        } as React.CSSProperties)
      : {};

    // Thêm hàm kiểm tra xem message có phải là greeting message hay không
    const isGreetingMessage = (message: ChatMessage): boolean => {
      // Kiểm tra dựa trên ID có chứa "welcome" hoặc "temp-welcome"
      if (message.id && message.id.includes("welcome")) {
        return true;
      }
      
      // Kiểm tra tin nhắn context-info theo ID mới
      if (message.id && message.id.includes("context-info")) {
        return true;
      }
      
      // Kiểm tra nếu là message đầu tiên của assistant và không có câu hỏi trước đó từ user
      if (message.role === "assistant" && messages.length <= 2 && messages[0]?.role === "assistant") {
        return true;
      }
      
      return false;
    };

    // Sửa lại hàm saveMessagesToLocalStorage để bỏ qua greeting messages tốt hơn
    const saveMessagesToLocalStorage = (messagesToSave: ChatMessage[]) => {
      try {
        // Không lưu lịch sử nếu skipSaveHistory = true
        if (skipSaveHistory) {
          return;
        }
        
        const expertId = contextData?.expertId || 'default';

        // Loại bỏ các tin nhắn system và welcome
        const filteredMessages = messagesToSave.filter(msg => {
          if (msg.role === 'system') return false;
          if (msg.id?.includes('welcome')) return false; // Sửa startsWith thành includes để lọc tốt hơn
          if (msg.isTyping === true) return false; // Loại bỏ cả tin nhắn đang typing
          return true;
        });
        
        // Chuẩn bị context info nếu có
        const contextInfo = {
          contextType: contextData?.contextType || null,
          contextId: contextData?.contextId || null,
          courseId: contextData?.course_contents?.id || contextData?.lessonData?.courseId || null,
          courseName: contextData?.course_contents?.title || contextData?.lessonData?.courseTitle || null,
        };

        // Check xem có tin nhắn nào sau khi lọc không
        if (filteredMessages.length === 0) {
          return;
        }
        
        // Đảm bảo tin nhắn đã được filter đúng cách
        if (filteredMessages.length > 0) {
          // Sử dụng dynamic import thay vì require
          import('@/services/OpenRouterServiceChat.js').then(module => {
            if (module && typeof module.saveChatHistory === 'function') {
              // Tạo danh sách tin nhắn lưu vào cookie với đúng định dạng
              const messagesForCookie = filteredMessages.map(msg => ({
                id: msg.id,
                role: msg.role,
                content: msg.content,
                timestamp: msg.timestamp instanceof Date ? msg.timestamp.toISOString() : 
                         typeof msg.timestamp === 'number' ? new Date(msg.timestamp).toISOString() : 
                         msg.timestamp,
                isTyping: false
              }));
              
              // Gọi hàm saveChatHistory với tham số đúng thứ tự
              module.saveChatHistory(
                expertId,
                messagesForCookie,
                contextInfo
              );
            }
          }).catch(error => {
            console.error("saveMessagesToLocalStorage: Lỗi khi import OpenRouterServiceChat.js:", error);
          });
        }
      } catch (error) {
        console.error('Error in saveMessagesToLocalStorage:', error);
      }
    };

    // Sửa lại renderMessage để hiển thị animation typing tốt hơn
    const renderMessage = (message: ChatMessage, index: number) => {
      // Đây là tin nhắn chào mừng đầu tiên
      const isWelcomeMessage = message.id && message.id.includes("welcome");
      const isTypingMessage = message.isTyping === true && message.role === "assistant";

      // Tạo key duy nhất cho message
      const messageKey = message.id ? 
        `${message.id}-${index}-${message.timestamp instanceof Date ? message.timestamp.getTime() : message.timestamp}` 
        : generateUniqueId(`msg-${index}`);

      return (
        <div
          key={messageKey}
          className={cn(
            "py-2 px-3 rounded-lg mb-2 relative chat-message",
            message.role === "user"
              ? "bg-blue-100 dark:bg-blue-900 ml-auto mr-0 max-w-[70%] self-end"
              : isWelcomeMessage
                ? "bg-gray-50 dark:bg-gray-800 mr-auto ml-0 max-w-[90%] welcome-message-container self-start"
                : "bg-gray-100 dark:bg-gray-700 mr-auto ml-0 max-w-[70%] self-start",
          )}
        >
          {/* Có thể thêm nội dung message header ở đây nếu cần */}

          <div className="flex items-start gap-2">
            {message.role === "assistant" && !isWelcomeMessage && (
              <Avatar className="h-7 w-7 mt-1 flex-shrink-0">
                <AvatarImage
                  src={
                    contextData?.expertAvatar ||
                    "https://imagedelivery.net/meCI2KDHmnUhVs_SxNm-Og/a1d82958-cc6f-4bdc-ce0b-25bdc7b27500/public"
                  }
                  alt={contextData?.expertName || "AI"}
                />
                <AvatarFallback>
                  {contextData?.expertName
                    ? contextData.expertName.charAt(0)
                    : "T"}
                </AvatarFallback>
              </Avatar>
            )}

            <div
              className={cn(
                "rounded-lg px-2 py-1 md:px-2.5 md:py-1.5 text-sm",
                message.role === "user"
                  ? "font-normal text-right"
                  : isWelcomeMessage
                    ? ""
                    : "bg-muted",
                "message-body",
                isWelcomeMessage ? "welcome-message-body w-full" : "flex-1",
              )}
            >
              {/* Nếu đang typing và không có nội dung, hiển thị dots animation */}
              {isTypingMessage && message.content === "" ? (
                <div className="typing-dots-container">
                  <div className="typing-dot"></div>
                  <div className="typing-dot"></div>
                  <div className="typing-dot"></div>
                </div>
              ) : (
                <div
                  className={cn(
                    "message-content markdown-content",
                    isWelcomeMessage ? "welcome-content" : "",
                  )}
                >
                  {message.content.startsWith("!typing") ? (
                    <div className="typing-indicator">
                      <span className="dot"></span>
                      <span className="dot"></span>
                      <span className="dot"></span>
                    </div>
                  ) : isHtmlContent(message.content) ? (
                    <div dangerouslySetInnerHTML={{ __html: message.content }} />
                  ) : (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: processMarkdownContent(message.content),
                      }}
                    />
                  )}
                </div>
              )}
              
              {/* Hiệu ứng typing sau nội dung tin nhắn AI đang nhập */}
              {isTypingMessage && message.content !== "" && (
                <div className="typing-indicator mt-1">
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                </div>
              )}
            </div>

            {message.role === "user" && (
              <Avatar className="h-8 w-8 mt-1 flex-shrink-0">
                <AvatarImage src="/assets/icons/user-avatar.png" alt="User" />
                <AvatarFallback>U</AvatarFallback>
              </Avatar>
            )}
          </div>
        </div>
      );
    };

    // Tạo hàm kiểm tra nếu là nội dung HTML
    const isHtmlContent = (content: string): boolean => {
      return /<\/?[a-z][\s\S]*>/i.test(content);
    };

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      clearConversation: () => {
        const storageKeyPrefix = getStorageKey();
        const expertId = contextData?.expertId || 'default';
        
        // Biến kiểm tra xem đã thêm welcome message hay chưa
        let welcomeMessageAdded = false;

        // Reset conversation ID
        setConversationId("");

        // Xóa tin nhắn và conversation_id khỏi localStorage
        localStorage.removeItem(`${storageKeyPrefix}-messages`);
        localStorage.removeItem(`${storageKeyPrefix}-conversation-id`);

        // Xóa tin nhắn trong state
        setMessages([]);

        // Thêm thông báo toast
        toast({
          title: t("ai:conversation_cleared"),
          description: t("ai:conversation_cleared_description"),
          duration: 3000,
        });

        console.log("Conversation cleared, conversation ID reset");

        // Xóa lịch sử từ cookie - nhiều cách để đảm bảo xóa triệt để
        try {
          // Sử dụng helper function để xóa cookie
          clearChatHistoryHelper(expertId);
          console.log(`clearConversation in useImperativeHandle: Cleared cookie for expert ${expertId}`);
          
          // Chờ một chút để đảm bảo cookie đã được xử lý xong
          setTimeout(() => {
            // Thêm tin nhắn chào mừng mới nếu chưa thêm
            if (!welcomeMessageAdded) {
              addWelcomeMessage();
              welcomeMessageAdded = true;
            }
          }, 300);
        } catch (error) {
          console.error("Error clearing cookie:", error);
          // Fallback: nếu có lỗi, thêm welcome message sau một chút thời gian
          if (!welcomeMessageAdded) {
            setTimeout(() => {
              addWelcomeMessage();
              welcomeMessageAdded = true;
            }, 300);
          }
        }
      },

      // Thêm phương thức mới để reset welcome message
      resetWelcomeMessage: () => {
        resetWelcomeMessage();
      },
    }));

    // Thêm useEffect để cập nhật lại tin nhắn welcome khi contextData.expertGreeting thay đổi
    useEffect(() => {
      // Kiểm tra các điều kiện cần thiết
      if (!contextData?.expertGreeting || messages.length === 0) {
        return;
      }
      
      // Kiểm tra xem tin nhắn đầu tiên có phải là welcome message không
      const firstMessage = messages[0];
      const isFirstMessageWelcome = firstMessage?.id && firstMessage.id.includes('welcome');
      
      if (isFirstMessageWelcome) {
        // Nếu nội dung thay đổi, cập nhật lại
        if (firstMessage.content !== contextData.expertGreeting) {
          const updatedWelcomeMessage = {
            ...firstMessage,
            content: contextData.expertGreeting,
            timestamp: new Date()
          };
          
          // Cập nhật danh sách tin nhắn, giữ nguyên các tin nhắn khác
          setMessages([updatedWelcomeMessage, ...messages.slice(1)]);
        }
      } else {
        // Nếu không có welcome message, thêm mới
        addWelcomeMessage();
      }
    }, [contextData.expertGreeting, messages]);

    // Sửa lại effect khi expert ID thay đổi
    useEffect(() => {
      // Chỉ thực hiện khi có expert ID
      if (!contextData?.expertId) return;
      
      // Thêm welcome message nếu không có tin nhắn nào
      if (messages.length === 0) {
        addWelcomeMessage();
      }
    }, [contextData.expertId, messages.length, addWelcomeMessage]);

    // Save conversation to localStorage
    useEffect(() => {
      if (messages.length > 0) {
        try {
          const storageKeyPrefix = getStorageKey();
          // Lọc bỏ các tin nhắn đang typing và giới hạn số lượng tin nhắn
          const messagesToSave = messages
            .filter((msg) => !msg.isTyping)
            .slice(-10); // Chỉ lưu 10 tin nhắn gần nhất

          localStorage.setItem(
            `${storageKeyPrefix}-messages`,
            JSON.stringify(messagesToSave),
          );
        } catch (error) {
          console.error("Error saving messages to localStorage:", error);
        }
      }

      if (conversationId) {
        const storageKeyPrefix = getStorageKey();
        localStorage.setItem(
          `${storageKeyPrefix}-conversation-id`,
          conversationId,
        );
      }
    }, [messages, conversationId, contextData]);

    // Cooldown timer
    useEffect(() => {
      if (cooldownRemaining > 0) {
        console.log(
          `Starting cooldown timer: ${cooldownRemaining}ms remaining`,
        );

        // Xóa timer cũ nếu có
        if (cooldownTimerRef.current) {
          clearInterval(cooldownTimerRef.current);
        }

        // Tạo timer mới
        cooldownTimerRef.current = setInterval(() => {
          setCooldownRemaining((prev) => {
            const newValue = prev - 1000;
            console.log(`Cooldown timer tick: ${newValue}ms remaining`);

            if (newValue <= 0) {
              // Xóa timer khi đếm ngược xong
              if (cooldownTimerRef.current) {
                clearInterval(cooldownTimerRef.current);
                cooldownTimerRef.current = null;
              }
              console.log("Cooldown timer finished");
              return 0;
            }
            return newValue;
          });
        }, 1000);
      } else if (cooldownRemaining === 0) {
        console.log("Cooldown is zero, clearing timer if exists");
        // Đảm bảo timer được xóa khi cooldown = 0
        if (cooldownTimerRef.current) {
          clearInterval(cooldownTimerRef.current);
          cooldownTimerRef.current = null;
        }
      }

      // Cleanup function
      return () => {
        if (cooldownTimerRef.current) {
          console.log("Cleaning up cooldown timer");
          clearInterval(cooldownTimerRef.current);
          cooldownTimerRef.current = null;
        }
      };
    }, [cooldownRemaining]);

    // Thay đổi hàm simulateTyping để cải thiện hiệu ứng
    const simulateTyping = (content: string, messageId: string) => {
      // Đảm bảo messageId luôn có giá trị
      const safeMessageId = messageId || generateUniqueId('ai-typing');
      
      // Loại bỏ chữ "text" ở đầu nếu có
      let cleanedContent = content;
      if (cleanedContent.startsWith("text")) {
        cleanedContent = cleanedContent.substring(4).trim();
      }

      // Loại bỏ code block nếu toàn bộ nội dung được bọc trong ```
      if (cleanedContent.startsWith("```") && cleanedContent.endsWith("```")) {
        cleanedContent = cleanedContent
          .substring(3, cleanedContent.length - 3)
          .trim();
      }

      // Thêm tin nhắn typing trống cho hiệu ứng ban đầu
      setMessages((prev) => {
        // Kiểm tra xem tin nhắn với ID này đã tồn tại chưa
        const messageExists = prev.some((msg) => msg.id === safeMessageId);
        if (messageExists) {
          return prev;
        }

        return [
          ...prev,
          {
            id: safeMessageId,
            role: "assistant" as "assistant",
            content: "",
            timestamp: new Date(),
            isTyping: true,
          },
        ];
      });

      let currentText = "";
      const words = cleanedContent.split(" ");
      let wordIndex = 0;

      // Tốc độ typing (ms) - có thể điều chỉnh nhanh hơn
      const typingSpeed = 20;

      const typeNextWord = () => {
        if (wordIndex < words.length) {
          currentText += (wordIndex > 0 ? " " : "") + words[wordIndex];
          wordIndex++;

          // Cập nhật tin nhắn với nội dung hiện tại
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === safeMessageId
                ? {
                    ...msg,
                    content: currentText,
                    isTyping: wordIndex < words.length,
                  }
                : msg,
            ),
          );

          // Tiếp tục typing
          typingTimerRef.current = setTimeout(typeNextWord, typingSpeed);
        } else {
          // Giữ trạng thái typing thêm một chút để UI mượt mà hơn
          setTimeout(() => {
            // Khi đã hoàn thành typing, đánh dấu tin nhắn không còn trong trạng thái typing
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === safeMessageId ? { ...msg, isTyping: false } : msg,
              ),
            );
          }, 300);
        }
      };

      // Chờ một chút trước khi bắt đầu typing để hiển thị dots
      typingTimerRef.current = setTimeout(typeNextWord, 500);
    };

    // Tạo một useEffect đặc biệt để theo dõi sự thay đổi của expert data và force render nếu cần
    useEffect(() => {
      // Nếu có expert greeting nhưng chưa hiển thị đúng, force render
      if (
        contextData?.expertGreeting &&
        messages.length > 0 &&
        messages[0].id === "welcome" &&
        messages[0].content !== contextData.expertGreeting
      ) {
        console.log(
          "ChatWithAI: Detected mismatch between displayed greeting and actual expert greeting, forcing render",
        );
        // Force render bằng cách tăng key
        setForceRenderKey((prev) => prev + 1);
        // Update greeting ngay lập tức
        const updatedWelcomeMessage = {
          ...messages[0],
          content: contextData.expertGreeting,
          timestamp: new Date(),
        };

        // Cập nhật messages
        const updatedMessages = [updatedWelcomeMessage, ...messages.slice(1)];
        setMessages(updatedMessages as ChatMessage[]);

        // Lưu vào localStorage
        try {
          saveMessagesToLocalStorage(updatedMessages as ChatMessage[]);
        } catch (error) {
          console.error(
            "ChatWithAI: Error saving updated welcome message:",
            error,
          );
        }
      }
    }, [contextData?.expertGreeting]);

    // Thêm style vào component
    useEffect(() => {
      // Chỉ thêm style một lần khi component mount
      const styleElement = document.createElement("style");
      styleElement.textContent = expert_greeting_style;
      document.head.appendChild(styleElement);

      // Cleanup khi component unmount
      return () => {
        document.head.removeChild(styleElement);
      };
    }, []);

    // Fetch user credits from Supabase
    useEffect(() => {
      const fetchUserCredits = async () => {
        if (!user?.id) return;
        
        try {
          const { data, error } = await supabase
            .from('users')
            .select('ai_credits')
            .eq('id', user.id)
            .single();
            
          if (error) {
            console.error('Error fetching user credits:', error);
            return;
          }
          
          setUserCredits(data.ai_credits || 0);
        } catch (error) {
          console.error('Error in fetchUserCredits:', error);
        }
      };

      fetchUserCredits();
    }, [user?.id]);

    // Tạo hàm để lấy AI credits từ user một cách an toàn
    const getUserAICredits = () => {
      return userCredits;
    };

    // Tạo CSS class cho footer dựa vào isMobileLayout
    const footerClassName = cn(
      isMobileLayout ? "mobile-chat-footer mobile-chat-footer-with-bottom-nav" : "",
      "flex-col"
    );
    
    // Tạo CSS class cho content dựa vào isMobileLayout
    const contentClassName = cn(
      "flex-1 overflow-y-auto py-3 px-3 md:px-4 messages-container",
      isMobileLayout ? "mobile-chat-content" : "",
      "fixed-height-scroll"
    );

    // Styles for mobile footer
    const mobileFooterStyles = {
      position: "fixed" as const,
      bottom: "70px", // Tăng giá trị này để tạo khoảng cách với thanh navigation
      left: 0,
      right: 0,
      backgroundColor: "var(--background)",
      zIndex: 40,
      borderTop: "1px solid var(--border)",
      padding: "10px 0",
    };

    // Style for mobile content to avoid overlap with footer
    const mobileChatContentStyles = {
      paddingBottom: "120px", // Giảm giá trị này để tránh khoảng trống thừa
    };

    // New styles for the actions before input
    const actionStyles = `
      .actions-before-input {
        display: flex;
        padding: 8px 16px;
        justify-content: space-between;
        align-items: center;
        border-top: 1px solid var(--border);
      }
      
      .clear-history-button {
        height: 36px;
        font-size: 0.875rem;
        display: flex;
        align-items: center;
        color: var(--muted-foreground);
        font-weight: 500;
        transition: all 0.2s;
      }
      
      .clear-history-button:hover {
        color: var(--foreground);
        background-color: var(--accent);
      }
      
      .clear-history-icon {
        margin-right: 6px;
      }
    `;

    // Handle submit from button
    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      console.log("Submit button clicked, input:", input, "Length:", input?.length || 0);
      if (passedHandleSubmit) {
        console.log("Using passedHandleSubmit");
        passedHandleSubmit(e);
      } else {
        console.log("Using handleSendMessage");
        handleSendMessage();
      }
    };

    // Kiểm tra và log kết quả API call
    useEffect(() => {
      if (isLoading) {
        console.log("API call is in progress");
      } else {
        console.log("API call state: loading =", isLoading);
      }
    }, [isLoading]);

    // Debug input - XÓA ĐOẠN LOG KHÔNG CẦN THIẾT
    // useEffect(() => {
    //   console.log("Input state changed:", {
    //     input: input,
    //     length: input?.length || 0,
    //     trimmedLength: input?.trim()?.length || 0,
    //     initialInput: initialInput
    //   });
    // }, [input, initialInput]);

    // Hàm sử dụng input state
    const handleSendMessage = async () => {
      console.log("Gửi tin nhắn với nội dung:", input);
      // Check if input is empty
      if (!input.trim()) return;
      
      // Tạo unique ID cho tin nhắn người dùng
      const userMessageId = generateUniqueId('user');
      
      // Thêm tin nhắn vào danh sách chat
      const userMessage = {
        id: userMessageId,
        role: "user" as "user",
        content: input.trim(),
        timestamp: new Date(),
      };
      
      // Tạo ID cho tin nhắn AI response
      const aiMessageId = generateUniqueId('ai');
      
      // Thêm tin nhắn người dùng vào state
      setMessages((prev) => [...prev, userMessage]);
      
      // Clear input
      setInput("");
      
      try {
        // Thêm tin nhắn chờ với animation typing
        const waitingMessage = {
          id: aiMessageId,
          role: "assistant" as "assistant",
          content: "",
          timestamp: new Date(),
          isTyping: true,
        };
        
        // Thêm tin nhắn đang chờ vào state
        setMessages((prev) => [...prev, waitingMessage]);
        
        // Đảm bảo scroll xuống cuối
        scrollToBottom();
        
        // Lấy expert ID từ context data
        const expertId = contextData?.expertId || "";
        
        // Chuyển sang gọi API trực tiếp đến ai-proxy endpoint
        console.log("Calling ai-proxy with:", { prompt: input.trim(), expert_id: expertId || null });
        
        // Chuẩn bị payload phù hợp
        const apiPayload = {
          prompt: input.trim(),
          chat_history: messages
            .filter(msg => !msg.isTyping && !msg.id?.includes("welcome"))
            .slice(-10)
            .map(msg => ({
              role: msg.role,
              content: msg.content,
            })),
          conversation_id: conversationId || ""
        };
        
        // Thêm expert_id vào payload nếu có giá trị hợp lệ
        if (expertId && expertId !== "default" && expertId !== "") {
          apiPayload["expert_id"] = expertId;
        }
        
        // Hàm giúp gọi API với timeout
        const fetchWithTimeout = async (url: string, options: any, timeout = 50000) => {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), timeout);
          
          try {
            const response = await fetch(url, {
              ...options,
              signal: controller.signal
            });
            clearTimeout(timeoutId);
            return response;
          } catch (error) {
            clearTimeout(timeoutId);
            throw error;
          }
        };
        
        // Hàm retry gọi API nếu thất bại
        const fetchWithRetry = async (url: string, options: any, retries = 2, timeout = 50000) => {
          let lastError;
          
          for (let i = 0; i <= retries; i++) {
            try {
              if (i > 0) {
                console.log(`Retry attempt ${i}/${retries}...`);
              }
              
              return await fetchWithTimeout(url, options, timeout);
            } catch (error) {
              console.error(`Attempt ${i + 1} failed:`, error);
              lastError = error;
              
              // Tăng timeout cho mỗi lần retry
              timeout = timeout * 1.5;
              
              // Đợi một chút trước khi retry
              if (i < retries) {
                const waitTime = 1000 * Math.pow(2, i);
                console.log(`Waiting ${waitTime}ms before retry...`);
                await new Promise(resolve => setTimeout(resolve, waitTime));
              }
            }
          }
          
          throw lastError;
        };
        
        // Gọi API với timeout và retry
        const response = await fetchWithRetry("/api/ai-proxy", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(apiPayload),
        });
        
        if (!response.ok) {
          throw new Error(`API request failed with status ${response.status}`);
        }
        
        const responseData = await response.json();
        console.log("Received response from AI:", responseData);
        
        // Lưu conversation ID nếu có
        if (responseData.conversation_id) {
          setConversationId(responseData.conversation_id);
        }
        
        // Nếu response có dữ liệu
        if (responseData && (responseData.answer !== undefined)) {
          // Đảm bảo answer không bao giờ là undefined, nếu API trả về chuỗi rỗng thì thay thế
          const answer = responseData.answer !== undefined && responseData.answer !== null 
            ? responseData.answer 
            : "Xin lỗi, thầy không thể xử lý yêu cầu của con lúc này.";
          
          console.log("Displaying AI response:", answer);
          
          // Tin nhắn phản hồi từ AI
          const aiResponseMessage = {
            id: aiMessageId, // Giữ nguyên ID để đảm bảo consistency
            role: "assistant" as "assistant",
            content: answer,
            timestamp: new Date(),
            isTyping: false,
          };
          
          // Cập nhật state
          setMessages((prev) => 
            prev.filter(msg => msg.id !== aiMessageId).concat(aiResponseMessage)
          );
          
          // Lưu lịch sử chat với delay nhỏ để đảm bảo state đã cập nhật
          try {
            // Bỏ qua nếu không cần lưu lịch sử
            if (!skipSaveHistory) {
              setTimeout(() => {
                const updatedMessages = [
                  ...messages.filter(msg => !msg.isTyping), // Loại bỏ các tin nhắn đang typing
                  userMessage,
                  aiResponseMessage
                ];
                
                // Lưu vào localStorage
                saveMessagesToLocalStorage(updatedMessages);
                console.log("Saved updated chat history with new messages", updatedMessages.length);
              }, 100);
            } else {
              console.log("Bỏ qua lưu lịch sử chat sau khi nhận phản hồi do skipSaveHistory=true");
            }
          } catch (error) {
            console.error("Lỗi khi lưu tin nhắn:", error);
          }
          
          // Thông báo cho component cha nếu có
          if (onMessageSent) {
            onMessageSent();
          }
        } else {
          // Nếu không có answer trong response
          console.error("No answer in response:", responseData);
          throw new Error("No answer in response");
        }
      } catch (error) {
        console.error("Lỗi khi gửi tin nhắn:", error);
        
        // Trích xuất thông tin lỗi chi tiết nếu có
        let errorMessage = "Có lỗi xảy ra khi gửi tin nhắn. Vui lòng thử lại sau.";
        let errorDetail = "";
        
        // Type assertion cho error để TypeScript không báo lỗi
        const err = error as any;
        
        if (err.response) {
          // Có phản hồi từ server nhưng là lỗi
          const status = err.response.status;
          
          if (status === 502) {
            // Xử lý đặc biệt cho lỗi Bad Gateway
            errorMessage = "Máy chủ không phản hồi kịp thời. Vui lòng thử lại hoặc chia nhỏ câu hỏi của bạn.";
            
            // Kiểm tra xem có thông tin chi tiết hơn không
            try {
              const errorData = err.response.data;
              if (errorData && errorData.error) {
                if (errorData.errorType === 'TIMEOUT') {
                  errorDetail = "Yêu cầu đã hết thời gian chờ. Thử chia nhỏ câu hỏi hoặc giảm độ phức tạp.";
                } else {
                  errorDetail = errorData.error;
                }
              }
            } catch (parseError) {
              console.error("Không thể phân tích dữ liệu lỗi:", parseError);
            }
          } else {
            errorMessage = `Lỗi ${status}: ${err.response.statusText || "Không xác định"}`;
          }
        } else if (err.request) {
          // Không nhận được phản hồi
          errorMessage = "Không nhận được phản hồi từ máy chủ. Vui lòng kiểm tra kết nối mạng và thử lại.";
        } else {
          // Lỗi khác
          errorMessage = (err as Error).message || "Lỗi không xác định";
        }
        
        // Tạo nội dung thông báo lỗi đầy đủ
        const fullErrorMessage = errorDetail 
          ? `${errorMessage}\n\n${errorDetail}\n\nGợi ý: Thử chia nhỏ câu hỏi hoặc đơn giản hóa yêu cầu.` 
          : `${errorMessage}\n\nGợi ý: Thử làm mới trang hoặc quay lại sau.`;
        
        // Xóa tin nhắn đang chờ và thêm thông báo lỗi
        setMessages((prev) => 
          prev.filter(msg => msg.id !== aiMessageId).concat({
            id: aiMessageId, // Giữ nguyên ID để đảm bảo consistency
            role: "assistant" as "assistant",
            content: fullErrorMessage,
            timestamp: new Date(),
            status: "error" as "error",
          })
        );
        
        // Hiển thị toast lỗi
        toast({
          title: "Lỗi",
          description: errorMessage,
          variant: "destructive",
        });
      } finally {
        // Đảm bảo xóa tin nhắn đang typing nếu có lỗi
        setMessages((prev) => {
          // Kiểm tra nếu vẫn có tin nhắn đang typing
          const hasTypingMessage = prev.some(msg => msg.id === aiMessageId && msg.isTyping);
          if (hasTypingMessage) {
            // Thêm tin nhắn lỗi thay thế
            return prev.filter(msg => msg.id !== aiMessageId).concat({
              id: aiMessageId,
              role: "assistant" as "assistant",
              content: "Xin lỗi, có vẻ như đã xảy ra lỗi. Vui lòng thử lại.",
              timestamp: new Date(),
              status: "error" as "error",
            });
          }
          return prev;
        });
        
        // Đảm bảo tắt loading state
        setIsLoading(false);
        
        // Đảm bảo scroll xuống cuối
        scrollToBottom();
      }
    };
    
    // Cập nhật MobileChatUI để sử dụng sendMessageDirectly
    const handleMobileMessage = (message: string) => {
      console.log("Received message from MobileChatUI:", message);
      if (message && message.trim()) {
        sendMessageDirectly(message);
      } else {
        console.log("Empty message from MobileChatUI");
      }
    };

    // Thêm hàm scrollToBottom để khắc phục lỗi reference
    const scrollToBottom = () => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ 
          behavior: "smooth", 
          block: "end"
        });
      }
    };

    const defaultSystemInstruction = `Bạn là một trợ lý AI của Buddha Diagrams, một nền tảng học Phật Pháp. Hãy trả lời các câu hỏi về Phật Pháp, lịch sử Phật giáo, và các khái niệm Phật giáo.

Hãy trả lời ngắn gọn và súc tích, thường không quá 2-3 đoạn văn. Nếu câu hỏi không liên quan đến Phật Pháp, hãy từ chối trả lời một cách lịch sự và gợi ý người dùng hỏi về các chủ đề Phật Pháp.

Knowledge cutoff: ${new Date().toISOString()}`;

    // Lưu tin nhắn vào localStorage khi component unmount
    useEffect(() => {
      return () => {
        // Bỏ qua nếu không cần lưu lịch sử
        if (skipSaveHistory) {
          return;
        }
        
        if (messages && messages.length > 0) {
          try {
            // Lọc bỏ các tin nhắn đang typing và welcome message trước khi lưu
            const messagesToSave = messages.filter(msg => {
              // Bỏ qua tin nhắn đang typing và tin nhắn welcome
              return !msg.isTyping && !(msg.id && msg.id.includes('welcome'));
            });
            
            if (messagesToSave.length > 0) {
              saveMessagesToLocalStorage(messagesToSave);
              // Chỉ log ngắn gọn số tin nhắn đã lưu
              console.log(`Đã lưu ${messagesToSave.length} tin nhắn vào localStorage khi unmount`);
            }
          } catch (error) {
            console.error("Lỗi khi lưu tin nhắn vào localStorage:", error);
          }
        }
      };
    }, [messages, skipSaveHistory]);

    // Thêm hàm resetWelcomeMessage để có thể gọi từ bên ngoài
    const resetWelcomeMessage = useCallback(() => {
      console.log(
        "Resetting welcome message for expert:",
        contextData?.expertName || "unknown",
      );
      
      // Kiểm tra xem đã có tin nhắn nào chưa (không tính tin nhắn welcome và context)
      const hasRealMessages = messages.some(msg => {
        // Từ chối tin nhắn welcome/context-info và chấp nhận các tin nhắn có ý nghĩa
        return !msg.id?.includes('welcome') && !msg.id?.includes('context-info') && !msg.isTyping;
      });
      
      if (hasRealMessages) {
        console.log("Đã có tin nhắn thực sự, giữ nguyên lịch sử chat và không thêm welcome message");
        return; // Có tin nhắn thực, không thêm welcome message
      }
      
      // Nếu chỉ có tin nhắn welcome/context, xóa chúng và thêm welcome message mới
      const onlyWelcomeMessages = messages.every(msg => 
        msg.id?.includes('welcome') || msg.id?.includes('context-info') || msg.isTyping
      );
      
      if (onlyWelcomeMessages && messages.length > 0) {
        console.log("Chỉ có tin nhắn welcome/context, xóa và thêm welcome message mới");
        setMessages([]); // Xóa các tin nhắn welcome cũ
        setTimeout(() => {
          addWelcomeMessage(); // Thêm welcome message mới
        }, 10);
      } else if (messages.length === 0) {
        console.log("Không có tin nhắn nào, thêm welcome message mới");
        addWelcomeMessage(); // Không có tin nhắn, thêm welcome message
      }
    }, [messages, addWelcomeMessage, contextData?.expertName, setMessages]);

    // Tạo một useEffect mới để xử lý trường hợp mở đầu, tập trung vào vấn đề greeting
    useEffect(() => {
      // Đảm bảo rằng chỉ chạy khi cần thiết - khi không có tin nhắn nào hoặc khi isWaitingForGreeting
      if (
        (messages.length === 0 || isWaitingForGreeting) &&
        contextData?.expertId
      ) {
        console.log(
          contextData.expertId,
        );

        // Kiểm tra xem expertGreeting đã sẵn sàng hay chưa
        if (contextData.expertGreeting) {
          // Nếu đang chờ greeting, reset lại
          if (isWaitingForGreeting) {
            setIsWaitingForGreeting(false);
          }

          // Nếu chưa có tin nhắn hoặc đang chờ, tạo tin nhắn welcome mới
          addWelcomeMessage();
        } else if (!isWaitingForGreeting && messages.length === 0) {
          // Nếu chưa có tin nhắn và expertGreeting chưa sẵn sàng, đánh dấu là đang chờ
          setIsWaitingForGreeting(true);

          // Thêm tin nhắn welcome tạm thời
          const tempGreeting = contextData?.expertName
            ? `Đang kết nối với ${contextData.expertName}...`
            : "Đang kết nối với chuyên gia...";

          setMessages([
            {
              id: generateUniqueId('temp-welcome'),
              role: "assistant" as "assistant",
              content: tempGreeting,
              timestamp: new Date(),
              isTyping: true,
            },
          ]);
        }
      }
    }, [
      messages.length,
      contextData?.expertId,
      contextData?.expertGreeting,
      contextData?.expertName,
      isWaitingForGreeting,
      addWelcomeMessage,
    ]);

    return (
      <>
        {isMobileLayout ? (
          // Mobile UI với unique key
          <MobileChatUI
            messages={messages.map((msg, index) => {
              const processedContent = msg.content ? 
                isHtmlContent(msg.content) ? msg.content : processMarkdownContent(msg.content) 
                : "";
              
              // Tạo key duy nhất cho mỗi tin nhắn
              // Đảm bảo ID luôn duy nhất và không thay đổi trong render cycle
              const stableId = msg.id || `msg-${index}-${new Date(msg.timestamp instanceof Date ? msg.timestamp : typeof msg.timestamp === 'number' ? msg.timestamp : Date.now()).getTime()}`;
              
              return {
                id: stableId,
                content: processedContent,
                sender: msg.role === 'assistant' ? 'expert' : 'user',
                timestamp: typeof msg.timestamp === 'number' 
                  ? msg.timestamp 
                  : (msg.timestamp instanceof Date ? msg.timestamp.getTime() : Date.now()),
                isLoading: msg.status === 'sending' || msg.isTyping === true
              };
            })}
            onSendMessage={handleMobileMessage}
            onClearChat={handleClearHistoryClick}
            onBack={() => window.history.back()}
            isLoading={isLoading}
            expertName={contextData?.expertName || title}
            expertTitle={contextData?.expert?.title || "Thiền sư AI"}
            expertAvatar={contextData?.expertAvatar}
            className={cn(className, hideTopHeader ? "pt-0" : "pt-0")}
            suggestedQuestions={contextData?.suggestedQuestions || []}
            hideHeader={hideTopHeader}
          />
        ) : (
          // UI Desktop hiện tại
          <Card
            className={cn(
              "flex flex-col h-full",
              isWidget ? "shadow-xl border-gray-200" : "",
              "chat-card-container",
              className,
            )}
            style={{
              height: fullHeight ? "100%" : undefined,
              border: isWidget ? "none" : undefined,
              boxShadow: isWidget ? "none" : undefined,
              borderRadius: isWidget ? 0 : undefined,
            }}
            key={forceRenderKey}
          >
            {/* Header chỉ hiển thị khi không phải mobile và không bị ẩn */}
            {!hideHeader && !isMobile && (
              <CardHeader className="px-4 py-3 border-b flex flex-row justify-between items-center shrink-0">
                <CardTitle className="text-xl font-semibold">{title}</CardTitle>
                <CreditDisplay />
              </CardHeader>
            )}

            {/* Content - Phần có thể scroll */}
            <CardContent
              ref={chatContainerRef}
              className={contentClassName}
            >
              {/* Hiển thị header trong nội dung chat cho mobile - đã loại bỏ cho mobile theo yêu cầu */}
              {!hideHeader && isMobile && !isMobileLayout && (
                <div className="chat-inner-header mb-3 sticky top-0 z-10">
                  <div className="flex items-center gap-2">
                    {toggleExpertsList && (
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={toggleExpertsList} 
                        className="h-8 w-8 p-0"
                      >
                        <Menu className="h-5 w-5" />
                        <span className="sr-only">{t("menu")}</span>
                      </Button>
                    )}
                    <Avatar className="h-8 w-8">
                      <AvatarImage
                        src={
                          contextData?.expertAvatar ||
                          "https://imagedelivery.net/meCI2KDHmnUhVs_SxNm-Og/a1d82958-cc6f-4bdc-ce0b-25bdc7b27500/public"
                        }
                        alt={contextData?.expertName || title}
                      />
                      <AvatarFallback>
                        {contextData?.expertName ? contextData.expertName.charAt(0) : title.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="font-medium">{title}</div>
                      <div className="text-xs text-gray-500">{t("ai.expert")}</div>
                    </div>
                    <CreditDisplay compact className="ml-auto" />
                  </div>
                </div>
              )}

              {isRateLimited && (
                <Alert variant="destructive" className="mb-2 text-sm">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{t("ai:rate_limit_exceeded")}</AlertDescription>
                </Alert>
              )}

              {error && (
                <Alert variant="destructive" className="mb-2 text-sm">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="flex flex-col space-y-2 ml-1 max-w overflow-hidden messages-wrapper">
                {messages.map(renderMessage)}

                {isWaitingForGreeting && (
                  <div className="text-center mt-2 mb-2">
                    <Skeleton className="h-20 w-full mb-2 rounded-md" />
                    <Skeleton className="h-3 w-3/4 mx-auto mb-1 rounded-md" />
                    <Skeleton className="h-3 w-2/4 mx-auto rounded-md" />
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </CardContent>

            {/* Footer - Cố định ở dưới cho mobile */}
            <CardFooter className={footerClassName}>
              {/* Nếu có renderActionsBeforeInput, render nó trước input */}
              {renderActionsBeforeInput && renderActionsBeforeInput()}

              {/* Buttons để clear history và change expert */}
              {!renderActionsBeforeInput && (
                <div className="w-full flex justify-end gap-2 pb-2">
                  <button
                    type="button"
                    className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 underline"
                    onClick={handleClearHistoryClick}
                  >
                    {t("clear_history", { ns: "ai" })}
                  </button>
                  
                  {/* Nếu có expert và toggleExpertsList, hiển thị nút change */}
                  {contextData?.expertName && toggleExpertsList && (
                    <button
                      type="button"
                      className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 underline"
                      onClick={handleChangeExpertClick}
                    >
                      {t("ai:change_expert")}
                    </button>
                  )}
                </div>
              )}

              {/* Phần input chat */}
              <div className={cn(
                "flex items-center space-x-2 py-2",
                isMobileLayout ? "w-full px-2" : "w-full px-4"
              )}>
                <Input
                  type="text"
                  placeholder={
                    isRateLimited
                      ? t("rate_limit_placeholder", { ns: "ai" })
                      : t("placeholder", { ns: "ai" })
                  }
                  value={input}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  disabled={isLoading || isRateLimited}
                  className={cn("flex-1", inputClassName)}
                />
                <Button
                  size="icon"
                  type="submit"
                  disabled={isLoading || isRateLimited || !input || !input.trim()}
                  onClick={(e) => {
                    console.log('Send button clicked, current input:', input);
                    if (input && input.trim()) {
                      if (handleSubmit) {
                        handleSubmit(e);
                      } else {
                        handleSendMessage();
                      }
                    } else {
                      console.log('Input is empty, not sending message');
                    }
                  }}
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </CardFooter>
          </Card>
        )}
      </>
    );
  },
);

export default ChatWithAI;
